import {
  ScrollArea,
  LoadingOverlay,
  Stack,
  Loader,
  Title,
  Paper,
  Group,
  TextInput,
  ActionIcon,
  rem,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { MessageBubble } from './chat-message-bubble';
import { ChatMessage } from '@src/types/chat';
import { useEffect, useRef, useState } from 'react';
import { AssistantMessage } from '@src/types/assistant';

interface ChatMessagesProps {
  assistantMessage: AssistantMessage;
  setAssistantMessage: (message: AssistantMessage) => void;
  threadId: string | null;
  loading: boolean;
  loadingText?: string;
  botResponse: string;
}
function ChatMessages({
  setAssistantMessage,
  // assistantMessage,
  threadId,
  botResponse,
  loading,
  loadingText,
}: ChatMessagesProps) {
  const [inputValue, setInputValue] = useState<string>('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const viewport = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!botResponse) return;
    const botMessage: ChatMessage = {
      id: chatMessages.length + 1,
      text: botResponse,
      isUser: false,
    };
    setChatMessages((prevMsgs) => [...prevMsgs, botMessage]);
  }, [botResponse]);

  useEffect(() => {
    // Scroll to the bottom whenever chatMessages changes
    viewport.current!.scrollTo({ top: viewport.current!.scrollHeight, behavior: 'smooth' });
  }, [chatMessages]);

  const sendMessage = async () => {
    if (!inputValue?.trim()) return;

    const userMessage: ChatMessage = {
      id: chatMessages.length + 1,
      text: inputValue,
      isUser: true,
    };
    setChatMessages((prevMsgs) => [...prevMsgs, userMessage]);
    // we can only reply on the same thread.
    if (threadId) {
      setAssistantMessage({ message: inputValue, threadId: threadId });
    }
    setInputValue('');
  };

  return (
    <div>
      <ScrollArea h="50vh" offsetScrollbars type="always" p={rem(0)} viewportRef={viewport}>
        <LoadingOverlay
          visible={loading && chatMessages.length === 0}
          loaderProps={{
            children: (
              <Stack align="center">
                <Loader size="xl" type="dots" />
                <Title c="dark.2">{loadingText}</Title>
              </Stack>
            ),
          }}
        />
        {chatMessages.length > 0 && (
          <Paper shadow="xs" p="md" withBorder w="100%">
            {chatMessages.map((chatMessage) => (
              <MessageBubble key={chatMessage.id} message={chatMessage} />
            ))}
          </Paper>
        )}
      </ScrollArea>
      <Group justify="center" mt="md">
        <TextInput
          w={rem(400)}
          variant="filled"
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          placeholder="Please tell me any changes you want to make..."
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
        />
        <ActionIcon size="lg" w={rem(50)} onClick={sendMessage} loading={loading}>
          <IconSend />
        </ActionIcon>
      </Group>
    </div>
  );
}

export { ChatMessages };
